import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Controller, useForm } from "react-hook-form";
import { endOfDay, format, isValid, parseISO, startOfDay } from "date-fns";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import Loading from "components/Loading";
import {
  MANUAL_ADJUSTMENT,
  SETTLEMENT_RAIL_NONE,
  SETTLEMENT_RAIL_OPTIONS,
  isAttributableBalanceRow,
  settlementRailForCreate,
} from "constants/partnerLedger";
import { PARTNER_TIERS, normalizePartnerTier } from "constants/partnerTiers";
import { useAuth } from "hooks/useAuth";
import {
  createPartnerLedgerEntry,
  getHttpsCallableErrorMessage,
  getPartnerLedgerBalances,
  listPartnerLedgerEntries,
} from "services/partnerLedgerService";

const partnerTierLabel = (tier) => {
  const normalized = normalizePartnerTier(tier);
  return (
    PARTNER_TIERS.find((item) => item.value === normalized)?.label ||
    normalized
  );
};

const formatCreatedAt = (iso) => {
  if (!iso) return "—";
  const date = parseISO(iso);
  if (!isValid(date)) return iso;
  return format(date, "yyyy-MM-dd HH:mm");
};

const FoundingPartnerAccountingPage = () => {
  const { organizationId } = useAuth();
  const [balances, setBalances] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadErrorMessage, setLoadErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [filterOrgId, setFilterOrgId] = useState("");
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);

  const { control, handleSubmit, reset, setValue, getValues } = useForm({
    defaultValues: {
      attributedOrgId: "",
      eventType: MANUAL_ADJUSTMENT,
      amount: "",
      currency: "USD",
      description: "",
      settlementRail: SETTLEMENT_RAIL_NONE,
    },
  });

  const loadLedger = useCallback(async () => {
    if (!organizationId) {
      setBalances([]);
      setEntries([]);
      setLoading(false);
      setLoadError(false);
      return;
    }
    setLoading(true);
    setLoadError(false);
    setLoadErrorMessage("");
    try {
      const [balancesResult, entriesResult] = await Promise.all([
        getPartnerLedgerBalances({ foundingPartnerOrgId: organizationId }),
        listPartnerLedgerEntries({ foundingPartnerOrgId: organizationId }),
      ]);
      setBalances(
        Array.isArray(balancesResult?.balances) ? balancesResult.balances : [],
      );
      setEntries(
        Array.isArray(entriesResult?.entries) ? entriesResult.entries : [],
      );
    } catch (err) {
      setLoadError(true);
      setLoadErrorMessage(
        getHttpsCallableErrorMessage(err, "Failed to load partner ledger."),
      );
      setBalances([]);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    loadLedger();
  }, [loadLedger]);

  const orgNameById = useMemo(() => {
    const map = {};
    balances.forEach((row) => {
      map[row.orgId] = row.orgName || row.orgId;
    });
    return map;
  }, [balances]);

  const currencyColumns = useMemo(() => {
    const set = new Set();
    balances.forEach((row) => {
      Object.keys(row.balances || {}).forEach((code) => set.add(code));
    });
    return Array.from(set).sort();
  }, [balances]);

  const attributedOrgOptions = useMemo(
    () =>
      balances.filter((row) =>
        isAttributableBalanceRow(row, organizationId),
      ),
    [balances, organizationId],
  );

  useEffect(() => {
    if (!attributedOrgOptions.length) return;
    const current = getValues("attributedOrgId");
    if (current && attributedOrgOptions.some((row) => row.orgId === current)) {
      return;
    }
    setValue("attributedOrgId", attributedOrgOptions[0]?.orgId || "");
  }, [attributedOrgOptions, getValues, setValue]);

  const filteredEntries = useMemo(() => {
    return entries.filter((entry) => {
      if (filterOrgId && entry.attributedOrgId !== filterOrgId) return false;
      if (!entry.createdAt) return !(filterStartDate || filterEndDate);
      const created = parseISO(entry.createdAt);
      if (!isValid(created)) return true;
      if (filterStartDate && created < startOfDay(filterStartDate)) return false;
      if (filterEndDate && created > endOfDay(filterEndDate)) return false;
      return true;
    });
  }, [entries, filterOrgId, filterStartDate, filterEndDate]);

  const onCreate = async (values) => {
    setSubmitting(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const amount = Number(values.amount);
      if (!Number.isInteger(amount) || amount === 0) {
        setErrorMessage(
          "Amount must be a non-zero integer in minor units (e.g. cents).",
        );
        return;
      }
      const result = await createPartnerLedgerEntry({
        foundingPartnerOrgId: organizationId,
        attributedOrgId: values.attributedOrgId,
        eventType: MANUAL_ADJUSTMENT,
        amount,
        currency: String(values.currency || "").trim().toUpperCase(),
        description: String(values.description || "").trim(),
        settlementRail: settlementRailForCreate(values.settlementRail),
      });
      setSuccessMessage(
        `Ledger entry recorded (${result.entryId}). This is an accounting record only — no money was moved.`,
      );
      reset({
        attributedOrgId: values.attributedOrgId,
        eventType: MANUAL_ADJUSTMENT,
        amount: "",
        currency: values.currency || "USD",
        description: "",
        settlementRail: SETTLEMENT_RAIL_NONE,
      });
      await loadLedger();
    } catch (err) {
      setErrorMessage(
        getHttpsCallableErrorMessage(err, "Could not record ledger entry."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !balances.length && !entries.length && !loadError) {
    return <Loading page />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <DashboardPageHeader
          title="Fusion Founder"
          subtitle="Auditable ledger balances and entries for your Fusion Founder organization and linked Fusion Partners/Affiliates. Settlement rails are not configured — recording an entry does not move money."
        />

        {successMessage && (
          <Alert
            severity="success"
            onClose={() => setSuccessMessage("")}
            sx={{ mb: 2 }}
          >
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert
            severity="error"
            onClose={() => setErrorMessage("")}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        )}
        {loadError && (
          <DataLoadErrorAlert
            message={loadErrorMessage || "Failed to load partner ledger."}
            onRetry={loadLedger}
          />
        )}

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Current balances
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Amounts are in minor units (e.g. cents). Empty cells mean no balance
            recorded for that currency.
          </Typography>
          {!loadError && !balances.length ? (
            <Typography color="text.secondary">
              No balance rows returned for this Fusion Founder hierarchy.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Organization</TableCell>
                    <TableCell>Partner tier</TableCell>
                    {currencyColumns.map((code) => (
                      <TableCell key={code} align="right">
                        {code}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {balances.map((row) => (
                    <TableRow key={row.orgId}>
                      <TableCell>
                        {row.orgName || row.orgId}
                        <Typography variant="body2" color="text.secondary">
                          {row.orgId}
                        </Typography>
                      </TableCell>
                      <TableCell>{partnerTierLabel(row.partnerTier)}</TableCell>
                      {currencyColumns.map((code) => (
                        <TableCell key={code} align="right">
                          {row.balances?.[code] != null
                            ? row.balances[code]
                            : "—"}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Record entry
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creates a non-authoritative manual adjustment for your org or a
            linked Fusion Partner/Affiliate. Entries are immutable. Settlement is
            not available yet.
          </Typography>
          <Stack
            component="form"
            spacing={2}
            onSubmit={handleSubmit(onCreate)}
            sx={{ maxWidth: 520 }}
          >
            <Controller
              name="attributedOrgId"
              control={control}
              rules={{ required: "Attributed organization is required." }}
              render={({ field, fieldState }) => (
                <FormControl fullWidth error={Boolean(fieldState.error)}>
                  <InputLabel id="fp-ledger-attributed-org">
                    Attributed organization
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="fp-ledger-attributed-org"
                    label="Attributed organization"
                  >
                    {attributedOrgOptions.map((row) => (
                      <MenuItem key={row.orgId} value={row.orgId}>
                        {row.orgName || row.orgId} (
                        {partnerTierLabel(row.partnerTier)})
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error ? (
                    <Typography variant="caption" color="error">
                      {fieldState.error.message}
                    </Typography>
                  ) : null}
                </FormControl>
              )}
            />
            <Controller
              name="eventType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="fp-ledger-event-type">Event type</InputLabel>
                  <Select
                    {...field}
                    labelId="fp-ledger-event-type"
                    label="Event type"
                  >
                    <MenuItem value={MANUAL_ADJUSTMENT}>
                      Manual adjustment
                    </MenuItem>
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name="amount"
              control={control}
              rules={{ required: "Amount is required." }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Amount (minor units)"
                  type="number"
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ||
                    "Non-zero integer in minor units (e.g. cents)."
                  }
                />
              )}
            />
            <Controller
              name="currency"
              control={control}
              rules={{
                required: "Currency is required.",
                pattern: {
                  value: /^[A-Za-z]{3}$/,
                  message: "Use a 3-letter ISO 4217 code.",
                },
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Currency"
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              rules={{ required: "Description is required." }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Description"
                  multiline
                  minRows={2}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="settlementRail"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="fp-ledger-settlement-rail">
                    Settlement rail
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="fp-ledger-settlement-rail"
                    label="Settlement rail"
                  >
                    {SETTLEMENT_RAIL_OPTIONS.map((option) => (
                      <MenuItem key={option.value || "none"} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <LoadingButton
                type="submit"
                variant="contained"
                loading={submitting}
                disabled={!attributedOrgOptions.length}
              >
                Record entry
              </LoadingButton>
              <Button disabled>
                Settlement not yet available
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Ledger entries
          </Typography>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <FormControl sx={{ minWidth: 220 }}>
              <InputLabel id="fp-ledger-filter-org">Attributed org</InputLabel>
              <Select
                labelId="fp-ledger-filter-org"
                label="Attributed org"
                value={filterOrgId}
                onChange={(e) => setFilterOrgId(e.target.value)}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {balances.map((row) => (
                  <MenuItem key={row.orgId} value={row.orgId}>
                    {row.orgName || row.orgId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DatePicker
              label="From date"
              value={filterStartDate}
              onChange={(date) => setFilterStartDate(date)}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label="To date"
              value={filterEndDate}
              onChange={(date) => setFilterEndDate(date)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Stack>

          {!loadError && !filteredEntries.length ? (
            <Typography color="text.secondary">
              No ledger entries match the current filters.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Organization</TableCell>
                    <TableCell>Event type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Currency</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Settlement rail</TableCell>
                    <TableCell>Settlement status</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Authoritative</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Settlement</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        {orgNameById[entry.attributedOrgId] ||
                          entry.attributedOrgId ||
                          "—"}
                      </TableCell>
                      <TableCell>{entry.eventType || "—"}</TableCell>
                      <TableCell align="right">
                        {entry.amount != null ? entry.amount : "—"}
                      </TableCell>
                      <TableCell>{entry.currency || "—"}</TableCell>
                      <TableCell>{entry.description || "—"}</TableCell>
                      <TableCell>{entry.settlementRail || "—"}</TableCell>
                      <TableCell>{entry.settlementStatus || "—"}</TableCell>
                      <TableCell>{entry.source || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={entry.authoritative ? "Yes" : "No"}
                          color={entry.authoritative ? "primary" : "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{formatCreatedAt(entry.createdAt)}</TableCell>
                      <TableCell>
                        <Button size="small" disabled>
                          Not yet available
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default FoundingPartnerAccountingPage;
